import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { auth } from '../lib/auth.js'
import { adminLogin } from '../lib/api.js'
import { aud } from '../lib/format.js'
import Icon from '../components/Icon.jsx'

const STATUS = {
  awaiting_payment: { label: 'Awaiting payment', tone: 'warn' },
  payment_review: { label: 'Verifying payment', tone: 'info' },
  paid: { label: 'Payment received', tone: 'ok' },
  fulfilled: { label: 'Completed', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
}

export default function Account() {
  const [customer, setCustomer] = useState(auth.customer())
  useEffect(() => auth.subscribe(() => setCustomer(auth.customer())), [])
  useEffect(() => { auth.refresh() }, [])

  return (
    <main className="container page page-narrow">
      <nav className="breadcrumbs"><Link to="/">Home</Link> / <span>My Account</span></nav>
      {customer ? <Dashboard customer={customer} /> : <AuthForms />}
    </main>
  )
}

/* ---------- login / register ---------- */

function AuthForms() {
  const navigate = useNavigate()
  const [mode, setMode] = useState('login')
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  // Staff use this same form: customer accounts are tried first, and only when those
  // credentials are not a customer's do we ask the admin side. That order matters —
  // an email that exists in both places belongs to the shopper here.
  async function loginStaff(customerError) {
    let session
    try {
      session = await adminLogin(form.email.trim(), form.password)
    } catch (adminError) {
      // Wrong admin credentials tell us nothing new; a switched-off staff account does.
      throw /อีเมลหรือรหัสผ่าน/.test(adminError.message) ? customerError : adminError
    }
    localStorage.setItem('lugs_admin_token', session.token)
    localStorage.removeItem('lugs_admin_key')
    navigate('/admin')
  }

  async function submit(e) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      if (mode === 'register') {
        await auth.register(form.email, form.password, form.name)
      } else {
        try {
          await auth.login(form.email, form.password)
        } catch (err) {
          await loginStaff(err)
        }
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <header className="page-head">
        <h1>My account</h1>
        <p>Log in to track your orders and check out faster with saved details.</p>
      </header>
      <div className="auth-card panel">
        <div className="auth-tabs" role="tablist">
          <button className={mode === 'login' ? 'active' : ''} onClick={() => { setMode('login'); setError('') }}>Log in</button>
          <button className={mode === 'register' ? 'active' : ''} onClick={() => { setMode('register'); setError('') }}>Create account</button>
        </div>
        <form onSubmit={submit}>
          {mode === 'register' && (
            <div className="field">
              <label htmlFor="ac-name">Full name</label>
              <input id="ac-name" value={form.name} onChange={set('name')} autoComplete="name" />
            </div>
          )}
          <div className="field">
            <label htmlFor="ac-email">Email *</label>
            <input id="ac-email" type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
          </div>
          <div className="field">
            <label htmlFor="ac-pass">Password * {mode === 'register' && <span className="muted">(at least 8 characters)</span>}</label>
            <input
              id="ac-pass" type="password" value={form.password} onChange={set('password')}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required minLength={8}
            />
          </div>
          {error && <p className="field-error">{error}</p>}
          <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'login' ? 'Log in' : 'Create account'}
          </button>
        </form>
        <p className="summary-note">
          <Icon name="shield" size={14} /> We only use your details for your orders — no spam, and you can still check out as a guest.
        </p>
      </div>
    </>
  )
}

/* ---------- logged-in dashboard ---------- */

function Dashboard({ customer }) {
  const navigate = useNavigate()
  const [orders, setOrders] = useState(null)
  const [form, setForm] = useState({
    name: customer.name || '', phone: customer.phone || '',
    address: customer.address || '', suburb: customer.suburb || '',
    state: customer.state || 'VIC', postcode: customer.postcode || '',
  })
  const [msg, setMsg] = useState('')
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  useEffect(() => {
    auth.orders().then((d) => setOrders(d.orders)).catch(() => setOrders([]))
  }, [])

  async function save(e) {
    e.preventDefault()
    setMsg('')
    try {
      await auth.saveProfile(form)
      setMsg('✓ Saved')
      setTimeout(() => setMsg(''), 2000)
    } catch (err) {
      setMsg(err.message)
    }
  }

  return (
    <>
      <header className="page-head account-head">
        <div>
          <h1>Hi, {customer.name?.split(' ')[0] || customer.email}</h1>
          <p>{customer.email}</p>
        </div>
        <button className="btn btn-outline" onClick={() => { auth.logout(); navigate('/') }}>Log out</button>
      </header>

      <div className="account-grid">
        <section className="panel">
          <h2><Icon name="box" size={19} /> My orders</h2>
          {orders === null ? (
            <p className="muted">Loading…</p>
          ) : orders.length === 0 ? (
            <div className="empty-state" style={{ padding: '30px 10px' }}>
              <p>No orders yet.</p>
              <Link to="/shop" className="btn btn-primary">Browse products</Link>
            </div>
          ) : (
            <div className="account-orders">
              {orders.map((o) => {
                const st = STATUS[o.status] || {}
                const count = JSON.parse(o.items).reduce((n, it) => n + it.qty, 0)
                return (
                  <Link key={o.id} to={`/order/${o.id}?t=${o.payment_token}`} className="account-order">
                    <div>
                      <strong>{o.payment_reference}</strong>
                      <small className="muted">
                        {new Date(o.created).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {' · '}{count} item{count > 1 ? 's' : ''} · {o.delivery === 'pickup' ? 'Pickup' : 'Delivery'}
                      </small>
                    </div>
                    <div className="account-order-right">
                      <span className={`status-chip status-${st.tone}`}>{st.label}</span>
                      <strong>{aud(o.total)}</strong>
                      <Icon name="chevronRight" size={16} />
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </section>

        <section className="panel">
          <h2><Icon name="pin" size={19} /> Saved details</h2>
          <p className="muted" style={{ marginBottom: 14 }}>Used to pre-fill checkout — update anytime.</p>
          <form onSubmit={save}>
            <div className="field">
              <label>Full name</label>
              <input value={form.name} onChange={set('name')} autoComplete="name" />
            </div>
            <div className="field">
              <label>Phone</label>
              <input value={form.phone} onChange={set('phone')} inputMode="tel" autoComplete="tel" />
            </div>
            <div className="field">
              <label>Street address</label>
              <input value={form.address} onChange={set('address')} autoComplete="street-address" />
            </div>
            <div className="field-row">
              <div className="field">
                <label>Suburb</label>
                <input value={form.suburb} onChange={set('suburb')} />
              </div>
              <div className="field field-sm">
                <label>Postcode</label>
                <input value={form.postcode} onChange={set('postcode')} inputMode="numeric" maxLength={4} />
              </div>
            </div>
            {msg && <p className={msg.startsWith('✓') ? 'admin-ok' : 'field-error'}>{msg}</p>}
            <button className="btn btn-primary" type="submit">Save details</button>
          </form>
        </section>
      </div>
    </>
  )
}
